import * as THREE from 'three';

const spiritCanvas = document.getElementById('spirit-canvas');

if (spiritCanvas) {
    let fadeTimeout;

    const showCanvas = () => {
        spiritCanvas.style.opacity = '1';
    };

    const handleInteraction = () => {
        clearTimeout(fadeTimeout);
        spiritCanvas.style.opacity = '0';
        // 3s to fade out + 3s to stay hidden = 6000ms
        fadeTimeout = setTimeout(showCanvas, 20000);
    };

    // Initial state
    spiritCanvas.style.opacity = '0';
    spiritCanvas.style.transition = 'opacity 3s ease-in-out';

    // Initial fade in after 3 seconds
    fadeTimeout = setTimeout(showCanvas, 3000);

    // Interaction listeners
    spiritCanvas.addEventListener('click', handleInteraction);
    spiritCanvas.addEventListener('touchstart', handleInteraction);

    // --- SETTINGS ---
    const profileName = spiritCanvas.getAttribute('data-profile') || 'default';

    const profiles = {
        // 'default': {
        //     speed: 2.0,
        //     dieSpeed: 0.015,
        //     radius: 0.8,
        //     curlSize: 0.04,
        //     attraction: 1.9,
        //     color1: '#ffffff',
        //     color2: '#ffffff'
        // },
        'mercury': {
            speed: 3.5,
            dieSpeed: 0.02,
            radius: 0.8,
            curlSize: 0.08,
            attraction: 1.0,
            color1: '#A9A9A9', // Grey
            color2: '#8B0000', // Dark Red
            flow: [0, 0, 0]
        },
        'venus': {
            speed: 0.5,
            dieSpeed: 0.005,
            radius: 0.8,
            curlSize: 0.01,
            attraction: 2.5,
            color1: '#FFFACD', // Lemon Chiffon (White-ish Yellow)
            color2: '#FFA500', // Orange
            flow: [0, 0, 0]
        },
        'earth': {
            speed: 1.5,
            dieSpeed: 0.015,
            radius: 0.8,
            curlSize: 0.03,
            attraction: 1.9,
            color1: '#4CAF50', // Vibrant Green
            color2: '#2196F3', // Vibrant Blue
            flow: [0, 0, 0]
        },
        'mars': {
            speed: 3.0,
            dieSpeed: 0.02,
            radius: 0.85,
            curlSize: 0.06,
            attraction: 1.2,
            color1: '#B7410E', // Rust
            color2: '#FF4500', // Orange Red
            flow: [0, 0, 0]
        },
        'jupiter': {
            speed: 1.2,
            dieSpeed: 0.01,
            radius: 1.2,
            curlSize: 0.04,
            attraction: 1.8,
            color1: '#F4A460', // Sandy Brown
            color2: '#8B4513', // Saddle Brown
            flow: [1.5, 0, 0]  // Horizontal flow
        },
        'saturn': {
            speed: 1.0,
            dieSpeed: 0.01,
            radius: 0.8,
            curlSize: 0.02,
            attraction: 2.0,
            color1: '#FFD700', // Gold
            color2: '#F5F5DC', // Beige
            flow: [0, 0, 0]
        },
        'uranus': {
            speed: 0.3,
            dieSpeed: 0.005,
            radius: 0.8,
            curlSize: 0.02,
            attraction: 2.5,
            color1: '#00FFFF', // Cyan
            color2: '#ADD8E6', // Light Blue
            flow: [0, 0, 0]
        },
        'neptune': {
            speed: 2.8,
            dieSpeed: 0.02,
            radius: 0.8,
            curlSize: 0.05,
            attraction: 1.5,
            color1: '#000080', // Navy
            color2: '#4B0082', // Indigo
            flow: [0, 0, 0]
        }
    };

    const selectedSettings = profiles[profileName] || profiles['default'];

    // --- SETTINGS ---
    const settings = {
        bgColor: '#000000',
        flow: [0, 0, 0],
        ...selectedSettings
    };

    // --- SHADER CHUNKS ---

    // Simplex Noise Derivatives 4D
    const simplexNoiseDerivatives4 = `
    vec4 mod289(vec4 x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    float mod289(float x) { return x - floor(x * (1.0 / 289.0)) * 289.0; }
    vec4 permute(vec4 x) { return mod289(((x*34.0)+1.0)*x); }
    float permute(float x) { return mod289(((x*34.0)+1.0)*x); }
    vec4 taylorInvSqrt(vec4 r) { return 1.79284291400159 - 0.85373472095314 * r; }
    float taylorInvSqrt(float r) { return 1.79284291400159 - 0.85373472095314 * r; }
    
    vec4 grad4(float j, vec4 ip) {
        const vec4 ones = vec4(1.0, 1.0, 1.0, -1.0);
        vec4 p,s;
        p.xyz = floor( fract (vec3(j) * ip.xyz) * 7.0) * ip.z - 1.0;
        p.w = 1.5 - dot(abs(p.xyz), ones.xyz);
        s = vec4(lessThan(p, vec4(0.0)));
        p.xyz = p.xyz + (s.xyz*2.0 - 1.0) * s.www;
        return p;
    }
    #define F4 0.309016994374947451
    vec4 snoise4(vec4 v) {
        const vec4  C = vec4( 0.138196601125011,0.276393202250021,0.414589803375032,-0.447213595499958);
        vec4 i  = floor(v + dot(v, vec4(F4)) );
        vec4 x0 = v -   i + dot(i, C.xxxx);
        vec4 i0;
        vec3 isX = step( x0.yzw, x0.xxx );
        vec3 isYZ = step( x0.zww, x0.yyz );
        i0.x = isX.x + isX.y + isX.z;
        i0.yzw = 1.0 - isX;
        i0.y += isYZ.x + isYZ.y;
        i0.zw += 1.0 - isYZ.xy;
        i0.z += isYZ.z;
        i0.w += 1.0 - isYZ.z;
        vec4 i3 = clamp( i0, 0.0, 1.0 );
        vec4 i2 = clamp( i0-1.0, 0.0, 1.0 );
        vec4 i1 = clamp( i0-2.0, 0.0, 1.0 );
        vec4 x1 = x0 - i1 + C.xxxx;
        vec4 x2 = x0 - i2 + C.yyyy;
        vec4 x3 = x0 - i3 + C.zzzz;
        vec4 x4 = x0 + C.wwww;
        i = mod289(i);
        float j0 = permute( permute( permute( permute(i.w) + i.z) + i.y) + i.x);
        vec4 j1 = permute( permute( permute( permute (
                 i.w + vec4(i1.w, i2.w, i3.w, 1.0 ))
               + i.z + vec4(i1.z, i2.z, i3.z, 1.0 ))
               + i.y + vec4(i1.y, i2.y, i3.y, 1.0 ))
               + i.x + vec4(i1.x, i2.x, i3.x, 1.0 ));
        vec4 ip = vec4(1.0/294.0, 1.0/49.0, 1.0/7.0, 0.0) ;
        vec4 p0 = grad4(j0,   ip);
        vec4 p1 = grad4(j1.x, ip);
        vec4 p2 = grad4(j1.y, ip);
        vec4 p3 = grad4(j1.z, ip);
        vec4 p4 = grad4(j1.w, ip);
        vec4 norm = taylorInvSqrt(vec4(dot(p0,p0), dot(p1,p1), dot(p2, p2), dot(p3,p3)));
        p0 *= norm.x;
        p1 *= norm.y;
        p2 *= norm.z;
        p3 *= norm.w;
        p4 *= taylorInvSqrt(dot(p4,p4));
        vec3 values0 = vec3(dot(p0, x0), dot(p1, x1), dot(p2, x2)); 
        vec2 values1 = vec2(dot(p3, x3), dot(p4, x4));
        vec3 m0 = max(0.5 - vec3(dot(x0,x0), dot(x1,x1), dot(x2,x2)), 0.0);
        vec2 m1 = max(0.5 - vec2(dot(x3,x3), dot(x4,x4)), 0.0);
        vec3 temp0 = -6.0 * m0 * m0 * values0;
        vec2 temp1 = -6.0 * m1 * m1 * values1;
        vec3 mmm0 = m0 * m0 * m0;
        vec2 mmm1 = m1 * m1 * m1;
        float dx = temp0[0] * x0.x + temp0[1] * x1.x + temp0[2] * x2.x + temp1[0] * x3.x + temp1[1] * x4.x + mmm0[0] * p0.x + mmm0[1] * p1.x + mmm0[2] * p2.x + mmm1[0] * p3.x + mmm1[1] * p4.x;
        float dy = temp0[0] * x0.y + temp0[1] * x1.y + temp0[2] * x2.y + temp1[0] * x3.y + temp1[1] * x4.y + mmm0[0] * p0.y + mmm0[1] * p1.y + mmm0[2] * p2.y + mmm1[0] * p3.y + mmm1[1] * p4.y;
        float dz = temp0[0] * x0.z + temp0[1] * x1.z + temp0[2] * x2.z + temp1[0] * x3.z + temp1[1] * x4.z + mmm0[0] * p0.z + mmm0[1] * p1.z + mmm0[2] * p2.z + mmm1[0] * p3.z + mmm1[1] * p4.z;
        float dw = temp0[0] * x0.w + temp0[1] * x1.w + temp0[2] * x2.w + temp1[0] * x3.w + temp1[1] * x4.w + mmm0[0] * p0.w + mmm0[1] * p1.w + mmm0[2] * p2.w + mmm1[0] * p3.w + mmm1[1] * p4.w;
        return vec4(dx, dy, dz, dw) * 49.0;
    }
    `;

    // Curl Noise 4D
    const curl4 = `
    ${simplexNoiseDerivatives4}

    vec3 curl( in vec3 p, in float noiseTime, in float persistence ) {
        vec4 xNoisePotentialDerivatives = vec4(0.0);
        vec4 yNoisePotentialDerivatives = vec4(0.0);
        vec4 zNoisePotentialDerivatives = vec4(0.0);

        for (int i = 0; i < 3; ++i) {
            float twoPowI = pow(2.0, float(i));
            float scale = 0.5 * twoPowI * pow(persistence, float(i));
            xNoisePotentialDerivatives += snoise4(vec4(p * twoPowI, noiseTime)) * scale;
            yNoisePotentialDerivatives += snoise4(vec4((p + vec3(123.4, 129845.6, -1239.1)) * twoPowI, noiseTime)) * scale;
            zNoisePotentialDerivatives += snoise4(vec4((p + vec3(-9519.0, 9051.0, -123.0)) * twoPowI, noiseTime)) * scale;
        }
        return vec3(
            zNoisePotentialDerivatives[1] - yNoisePotentialDerivatives[2],
            xNoisePotentialDerivatives[2] - zNoisePotentialDerivatives[0],
            yNoisePotentialDerivatives[0] - xNoisePotentialDerivatives[1]
        );
    }
    `;

    // --- SETUP ---
    let width = spiritCanvas.clientWidth || window.innerWidth;
    let height = spiritCanvas.clientHeight || window.innerHeight;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true, preserveDrawingBuffer: false });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, height);
    renderer.autoClear = false;
    spiritCanvas.innerHTML = '';
    spiritCanvas.appendChild(renderer.domElement);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(45, width / height, 1, 1000);
    camera.position.set(0, 0, 300);

    const TEXTURE_WIDTH = 128; // Reduced count
    const TEXTURE_HEIGHT = 128;
    const AMOUNT = TEXTURE_WIDTH * TEXTURE_HEIGHT;

    // --- SIMULATION (FBO) ---

    // Quad Vertex Shader
    const quadVert = `
    precision highp float;
    attribute vec3 position;
    attribute vec2 uv;
    uniform mat4 projectionMatrix;
    uniform mat4 modelViewMatrix;
    varying vec2 vUv;
    void main() {
        vUv = uv;
        gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
    }
    `;

    // Simulation Fragment Shader
    const posFrag = `
    precision highp float;
    uniform vec2 resolution;
    uniform sampler2D texturePosition;
    uniform sampler2D textureDefaultPosition;
    uniform float time;
    uniform float speed;
    uniform float dieSpeed;
    uniform float radius;
    uniform float curlSize;
    uniform float attraction;
    uniform float initAnimation;
    uniform vec3 mouse3d;
    uniform vec3 flow;
    varying vec2 vUv;

    ${curl4}

    void main() {
        vec2 uv = vUv;
        vec4 positionInfo = texture2D( texturePosition, uv );
        vec3 position = mix(vec3(0.0, -200.0, 0.0), positionInfo.xyz, smoothstep(0.0, 0.3, initAnimation));
        float life = positionInfo.a - dieSpeed;
        
        vec3 followPosition = mix(vec3(0.0, -0.0, 0.0), mouse3d, smoothstep(0.2, 0.7, initAnimation));

        if(life < 0.0) {
            vec4 defPosInfo = texture2D( textureDefaultPosition, uv );
            position = defPosInfo.xyz * (1.0 + sin(time * 15.0) * 0.2) * radius;
            position += followPosition;
            life = 0.5 + fract(defPosInfo.w * 21.4131 + time);
        } else {
            vec3 delta = followPosition - position;
            // Attraction
            position += delta * (0.005 + life * 0.01) * attraction * (1.0 - smoothstep(50.0, 350.0, length(delta))) * speed;
            // Curl
            position += curl(position * curlSize, time, 0.1 + (1.0 - life) * 0.1) * speed;
            // Flow
            position += flow * speed;
        }

        gl_FragColor = vec4(position, life);
    }
    `;

    // Render Target Setup
    function getRenderTarget() {
        return new THREE.WebGLRenderTarget(TEXTURE_WIDTH, TEXTURE_HEIGHT, {
            wrapS: THREE.ClampToEdgeWrapping,
            wrapT: THREE.ClampToEdgeWrapping,
            minFilter: THREE.NearestFilter,
            magFilter: THREE.NearestFilter,
            format: THREE.RGBAFormat,
            type: THREE.HalfFloatType,
            stencilBuffer: false,
            depthBuffer: false
        });
    }

    let rtPosition1 = getRenderTarget();
    let rtPosition2 = getRenderTarget();

    // Simulation Mesh
    const simScene = new THREE.Scene();
    const simCamera = new THREE.Camera();
    simCamera.position.z = 1;

    const simMaterial = new THREE.RawShaderMaterial({
        uniforms: {
            resolution: { value: new THREE.Vector2(TEXTURE_WIDTH, TEXTURE_HEIGHT) },
            texturePosition: { value: null },
            textureDefaultPosition: { value: null },
            time: { value: 0 },
            speed: { value: settings.speed },
            dieSpeed: { value: settings.dieSpeed },
            radius: { value: settings.radius },
            curlSize: { value: settings.curlSize },
            attraction: { value: settings.attraction },
            initAnimation: { value: 1 },
            mouse3d: { value: new THREE.Vector3(0, 0, 0) },
            flow: { value: new THREE.Vector3().fromArray(settings.flow) }
        },
        vertexShader: quadVert,
        fragmentShader: posFrag,
        blending: THREE.NoBlending
    });

    const simMesh = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), simMaterial);
    simScene.add(simMesh);

    // Initial Positions
    const dtPosition = new Float32Array(AMOUNT * 4);
    for (let i = 0; i < AMOUNT; i++) {
        let r = (0.5 + Math.random() * 0.5) * 50;
        let phi = (Math.random() - 0.5) * Math.PI;
        let theta = Math.random() * Math.PI * 2;
        dtPosition[i * 4 + 0] = r * Math.cos(theta) * Math.cos(phi);
        dtPosition[i * 4 + 1] = r * Math.sin(phi);
        dtPosition[i * 4 + 2] = r * Math.sin(theta) * Math.cos(phi);
        dtPosition[i * 4 + 3] = Math.random();
    }
    const textureDefaultPosition = new THREE.DataTexture(dtPosition, TEXTURE_WIDTH, TEXTURE_HEIGHT, THREE.RGBAFormat, THREE.FloatType);
    textureDefaultPosition.needsUpdate = true;
    simMaterial.uniforms.textureDefaultPosition.value = textureDefaultPosition;

    // Fill first RT
    simMaterial.uniforms.texturePosition.value = textureDefaultPosition;
    renderer.setRenderTarget(rtPosition1);
    renderer.render(simScene, simCamera);
    renderer.setRenderTarget(null);


    // --- PARTICLES (RENDER) ---

    const particlesVert = `
    precision highp float;
    uniform sampler2D texturePosition;
    uniform mat4 modelMatrix;
    uniform mat4 viewMatrix;
    uniform mat4 projectionMatrix;
    attribute vec3 position; 
    varying float vLife;
    
    void main() {
        vec4 posInfo = texture2D(texturePosition, position.xy);
        vec4 worldPosition = modelMatrix * vec4(posInfo.xyz, 1.0);
        vec4 mvPosition = viewMatrix * worldPosition;
        vLife = posInfo.w;
        // Size attenuation (larger)
        gl_PointSize = 5000.0 / length(mvPosition.xyz) * smoothstep(0.0, 0.2, posInfo.w);
        gl_Position = projectionMatrix * mvPosition;
    }
    `;

    const particlesFrag = `
    precision highp float;
    varying float vLife;
    uniform vec3 color1;
    uniform vec3 color2;

    float sdHexagram( in vec2 p, in float r )
    {
        const vec4 k = vec4(-0.5,0.8660254038,0.5773502692,1.73205081);
        p = abs(p);
        p -= 2.0*min(dot(k.xy,p),0.0)*k.xy;
        p -= 2.0*min(dot(k.yx,p),0.0)*k.yx;
        p -= vec2(clamp(p.x,r*k.z,r*k.w),r);
        return length(p)*sign(p.y);
    }

    void main() {
        vec2 coord = gl_PointCoord - vec2(0.5);
        
        // Star shape using Hexagram SDF
        float dist = sdHexagram(coord, 0.15); 
        if(dist > 0.0) discard;
        
        float alpha = smoothstep(0.0, -0.1, dist);
        alpha = pow(alpha, 1.5);

        vec3 color = mix(color2, color1, smoothstep(0.0, 0.7, vLife));
        gl_FragColor = vec4(color, alpha * smoothstep(0.0, 0.1, vLife));
    }
    `;

    const refPositions = new Float32Array(AMOUNT * 3);
    for (let i = 0; i < AMOUNT; i++) {
        refPositions[i * 3 + 0] = (i % TEXTURE_WIDTH) / TEXTURE_WIDTH;
        refPositions[i * 3 + 1] = Math.floor(i / TEXTURE_WIDTH) / TEXTURE_HEIGHT;
        refPositions[i * 3 + 2] = 0;
    }
    const partGeom = new THREE.BufferGeometry();
    partGeom.setAttribute('position', new THREE.BufferAttribute(refPositions, 3));

    const partMaterial = new THREE.RawShaderMaterial({
        uniforms: {
            texturePosition: { value: null },
            color1: { value: new THREE.Color(settings.color1) },
            color2: { value: new THREE.Color(settings.color2) }
        },
        vertexShader: particlesVert,
        fragmentShader: particlesFrag,
        transparent: true,
        blending: THREE.AdditiveBlending,
        depthTest: false,
        depthWrite: false
    });

    const particleMesh = new THREE.Points(partGeom, partMaterial);
    scene.add(particleMesh);

    // --- LOOP ---
    const clock = new THREE.Clock();

    // INPUT LOGIC
    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2(-1000, -1000); // Offscreen default
    let isPressed = false;

    const onMove = (x, y) => {
        const rect = spiritCanvas.getBoundingClientRect();
        const cx = x - rect.left;
        const cy = y - rect.top;
        mouse.x = (cx / width) * 2 - 1;
        mouse.y = -(cy / height) * 2 + 1;
    };

    const onPress = () => { isPressed = true; };
    const onRelease = () => { isPressed = false; };

    // Mouse events
    spiritCanvas.addEventListener('mousemove', e => onMove(e.clientX, e.clientY));
    spiritCanvas.addEventListener('mousedown', onPress);
    window.addEventListener('mouseup', onRelease);

    // Touch events
    spiritCanvas.addEventListener('touchmove', e => {
        if (e.touches.length > 0) {
            onMove(e.touches[0].clientX, e.touches[0].clientY);
        }
    }, { passive: true });
    spiritCanvas.addEventListener('touchstart', onPress, { passive: true });
    window.addEventListener('touchend', onRelease);


    function animate() {
        requestAnimationFrame(animate);

        const delta = Math.min(clock.getDelta(), 0.1);
        const time = clock.elapsedTime;

        // --- INTERACTION ---
        raycaster.setFromCamera(mouse, camera);
        const planeZ = new THREE.Plane(new THREE.Vector3(0, 0, 1), 0);
        const intersect = new THREE.Vector3();
        raycaster.ray.intersectPlane(planeZ, intersect);

        let target = new THREE.Vector3(0, 0, 0); // Default center
        // Only if pressed we track intersection
        if (isPressed && intersect) {
            target.copy(intersect);
        }

        // Lerp attraction point
        simMaterial.uniforms.mouse3d.value.lerp(target, 0.1);

        // --- SIMULATION STEP ---
        const currentRT = rtPosition1;
        const nextRT = rtPosition2;

        simMaterial.uniforms.texturePosition.value = currentRT.texture;
        simMaterial.uniforms.time.value = time;
        simMaterial.uniforms.speed.value = settings.speed;
        simMaterial.uniforms.dieSpeed.value = settings.dieSpeed;
        simMaterial.uniforms.radius.value = settings.radius;
        simMaterial.uniforms.curlSize.value = settings.curlSize;

        renderer.setRenderTarget(nextRT);
        renderer.render(simScene, simCamera);
        renderer.setRenderTarget(null);

        // Swap
        rtPosition1 = nextRT;
        rtPosition2 = currentRT;

        // --- RENDER STEP ---
        partMaterial.uniforms.texturePosition.value = rtPosition1.texture;

        // Rotate container slightly
        scene.rotation.y = time * 0.05;

        renderer.clear();
        renderer.render(scene, camera);
    }

    animate();

    // Resize
    window.addEventListener('resize', () => {
        if (!spiritCanvas) return;
        width = spiritCanvas.clientWidth || window.innerWidth;
        height = spiritCanvas.clientHeight || 600;
        camera.aspect = width / height;
        camera.updateProjectionMatrix();
        renderer.setSize(width, height);
    });
}
